import { generateAudioWsUrl } from "../ENV";
import { getUserToken } from "./Utils";

let currentWS = null;
let currentObjectUrl = null;
let currentGeneration = 0;

/** Enable in dev, or run `localStorage.setItem('TTS_DEBUG', '1')` in browser console */
const TTS_DEBUG =
  process.env.NODE_ENV === "development" ||
  (typeof localStorage !== "undefined" && localStorage.getItem("TTS_DEBUG") === "1");

/** Last stream diagnostics — inspect via `window.__TTS_DEBUG_REPORT__` or getTTSDebugReport() */
let lastDebugReport = null;

function createDebugReport(text, langCode) {
  return {
    startedAt: Date.now(),
    textLength: text?.length ?? 0,
    langCode,
    chunksReceived: 0,
    bytesReceived: 0,
    appendSuccesses: 0,
    bytesAppended: 0,
    appendFailures: [],
    chunksLostOnAppend: 0,
    bytesLostOnAppend: 0,
    queueLengthAtComplete: null,
    sourceBufferUpdatingAtComplete: null,
    endOfStreamCalled: false,
    endOfStreamError: null,
    mediaSourceStateAtEnd: null,
    wsCloseCode: null,
    stoppedExternally: false,
    stopReason: null,
    staleCallbacksIgnored: 0,
    resolvedAt: null,
    completedAt: null,
  };
}

function debugLog(report, ...args) {
  if (!TTS_DEBUG) return;
  console.log("[TTS-WS]", ...args);
}

function debugWarn(report, ...args) {
  if (!TTS_DEBUG) return;
  console.warn("[TTS-WS]", ...args);
}

function debugError(report, ...args) {
  if (!TTS_DEBUG) return;
  console.error("[TTS-WS]", ...args);
}

function publishReport(report) {
  lastDebugReport = report;
  if (TTS_DEBUG && typeof window !== "undefined") {
    window.__TTS_DEBUG_REPORT__ = report;
  }
}

function logSummary(report, label) {
  if (!TTS_DEBUG) return;
  const appendGap = report.bytesReceived - report.bytesAppended - report.bytesLostOnAppend;
  console.group(`[TTS-WS] ${label}`);
  console.table({
    chunksReceived: report.chunksReceived,
    bytesReceived: report.bytesReceived,
    appendSuccesses: report.appendSuccesses,
    bytesAppended: report.bytesAppended,
    appendFailures: report.appendFailures.length,
    bytesLostOnAppend: report.bytesLostOnAppend,
    bytesNotAppended: appendGap > 0 ? appendGap : 0,
    queueAtComplete: report.queueLengthAtComplete,
    endOfStreamCalled: report.endOfStreamCalled,
    stoppedExternally: report.stoppedExternally,
    wsCloseCode: report.wsCloseCode,
  });
  if (report.appendFailures.length > 0) {
    console.log("appendFailures detail:", report.appendFailures);
  }
  if (report.bytesLostOnAppend > 0 || report.appendFailures.length > 0) {
    console.warn(
      "Likely cause: SourceBuffer.appendBuffer failed — MP3 chunk boundary or MSE issue"
    );
  } else if (report.stoppedExternally) {
    console.warn("Likely cause: stream stopped externally —", report.stopReason);
  } else if (report.queueLengthAtComplete > 0) {
    console.warn("Likely cause: chunks still in queue when complete arrived");
  } else if (!report.endOfStreamCalled && report.chunksReceived > 0) {
    console.warn("Likely cause: endOfStream() never called");
  } else if (report.bytesReceived > 0 && report.bytesAppended === report.bytesReceived) {
    console.log(
      "All WS bytes were appended. If audio still partial, check HTMLAudioElement duration/ended or backend MP3 content."
    );
  }
  console.groupEnd();
}

export function getTTSDebugReport() {
  return lastDebugReport;
}

export function stopCurrentTTSStream(reason = "unknown") {
  if (lastDebugReport && !lastDebugReport.stoppedExternally) {
    lastDebugReport.stoppedExternally = true;
    lastDebugReport.stopReason = reason;
    debugWarn(lastDebugReport, "stopCurrentTTSStream:", reason);
    if (TTS_DEBUG) {
      console.trace("[TTS-WS] stop stack");
    }
    logSummary(lastDebugReport, "stopped externally");
  }
  currentGeneration += 1;
  if (currentWS) {
    try { currentWS.close(1000); } catch (e) {}
    currentWS = null;
  }
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }
}

export function streamSpeechWS(text, langCode) {
  if (
    !window.MediaSource ||
    !MediaSource.isTypeSupported("audio/mpeg")
  ) {
    debugLog(null, "MediaSource unsupported — REST fallback");
    return Promise.resolve(null);
  }

  currentGeneration += 1;
  const myGen = currentGeneration;
  const report = createDebugReport(text, langCode);
  publishReport(report);

  // Abort any previous stream without incrementing generation again
  if (currentWS) {
    try { currentWS.close(1000); } catch (e) {}
    currentWS = null;
  }
  if (currentObjectUrl) {
    URL.revokeObjectURL(currentObjectUrl);
    currentObjectUrl = null;
  }

  return new Promise((resolve) => {
    const token = getUserToken();
    const base = generateAudioWsUrl.replace(/\/$/, "");
    const wsUrl = `${base}?token=${encodeURIComponent(token)}&lang_code=${encodeURIComponent(langCode)}`;

    debugLog(report, "connecting", wsUrl.replace(/token=[^&]+/, "token=***"));

    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    currentWS = ws;

    const mediaSource = new MediaSource();
    const objectUrl = URL.createObjectURL(mediaSource);
    currentObjectUrl = objectUrl;

    let sourceBuffer = null;
    const queue = [];
    let streamComplete = false;
    let resolved = false;

    function isStale() {
      if (myGen !== currentGeneration) {
        report.staleCallbacksIgnored += 1;
        return true;
      }
      return false;
    }

    function safeResolve(value) {
      if (!resolved) {
        resolved = true;
        report.resolvedAt = Date.now();
        resolve(value);
      }
    }

    function cleanup(revokeUrl = true) {
      if (currentWS === ws) currentWS = null;
      if (revokeUrl && currentObjectUrl === objectUrl) {
        URL.revokeObjectURL(objectUrl);
        currentObjectUrl = null;
      }
      try { ws.close(); } catch (e) {}
    }

    function appendChunk(chunk, source) {
      const byteLength = chunk.byteLength;
      try {
        sourceBuffer.appendBuffer(chunk);
        report.appendSuccesses += 1;
        report.bytesAppended += byteLength;
        debugLog(
          report,
          `append OK (${source}) chunk #${report.chunksReceived} ${byteLength}B, queue=${queue.length}`
        );
        return true;
      } catch (e) {
        report.appendFailures.push({
          source,
          chunkIndex: report.chunksReceived,
          byteLength,
          name: e.name,
          message: e.message,
        });
        debugError(
          report,
          `append FAILED (${source}) chunk #${report.chunksReceived}`,
          e.name,
          e.message,
          `${byteLength}B`
        );
        return false;
      }
    }

    function processQueue() {
      if (isStale()) return;
      if (!sourceBuffer) return;

      if (queue.length > 0 && !sourceBuffer.updating) {
        const chunk = queue.shift();
        if (!appendChunk(chunk, "queue")) {
          // Re-queue failed chunk — do not drop (also helps diagnose stuck queue)
          queue.unshift(chunk);
        }
        return;
      }

      if (
        queue.length === 0 &&
        streamComplete &&
        !sourceBuffer.updating &&
        mediaSource.readyState === "open"
      ) {
        try {
          mediaSource.endOfStream();
          report.endOfStreamCalled = true;
          report.mediaSourceStateAtEnd = mediaSource.readyState;
          report.completedAt = Date.now();
          debugLog(report, "endOfStream() called");
          logSummary(report, "stream complete");
        } catch (e) {
          report.endOfStreamError = { name: e.name, message: e.message };
          debugError(report, "endOfStream FAILED", e.name, e.message);
          logSummary(report, "endOfStream failed");
        }
      }
    }

    mediaSource.addEventListener("sourceopen", () => {
      if (isStale()) return;
      try {
        sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
        sourceBuffer.addEventListener("updateend", processQueue);
        debugLog(report, "sourceopen — SourceBuffer ready");
        processQueue();
      } catch (e) {
        debugError(report, "addSourceBuffer failed", e);
        cleanup();
        safeResolve(null);
      }
    });

    ws.onopen = () => {
      if (isStale()) { ws.close(1000); return; }
      debugLog(report, "ws open — sending text", `${report.textLength} chars`);
      ws.send(JSON.stringify({ type: "text", data: { text } }));
    };

    ws.onmessage = (event) => {
      if (isStale()) return;

      if (event.data instanceof ArrayBuffer) {
        report.chunksReceived += 1;
        report.bytesReceived += event.data.byteLength;

        if (!resolved) {
          safeResolve({ url: objectUrl });
          debugLog(report, "promise resolved — first chunk", `${event.data.byteLength}B`);
        }

        if (sourceBuffer && !sourceBuffer.updating) {
          if (!appendChunk(event.data, "direct")) {
            queue.push(event.data);
            processQueue();
          }
        } else {
          queue.push(event.data);
          debugLog(
            report,
            `chunk queued #${report.chunksReceived}`,
            `${event.data.byteLength}B, queue=${queue.length}, updating=${sourceBuffer?.updating}`
          );
        }
        return;
      }

      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "complete") {
          report.queueLengthAtComplete = queue.length;
          report.sourceBufferUpdatingAtComplete = sourceBuffer?.updating ?? null;
          debugLog(
            report,
            "complete received",
            `queue=${queue.length}, updating=${sourceBuffer?.updating}, bytesReceived=${report.bytesReceived}`
          );

          ws.close(1000);
          if (currentWS === ws) currentWS = null;
          streamComplete = true;
          processQueue();

          if (!resolved) {
            safeResolve({ url: objectUrl });
          }
        } else if (msg.type === "error") {
          debugError(report, "server error", msg.message);
          logSummary(report, "server error");
          cleanup();
          safeResolve({ data: { detail: msg.message || "Audio synthesis failed" } });
        }
      } catch (e) {
        debugWarn(report, "malformed text frame", event.data);
      }
    };

    ws.onerror = () => {
      if (isStale()) return;
      debugError(report, "ws error");
      logSummary(report, "ws error");
      cleanup();
      safeResolve(null);
    };

    ws.onclose = (event) => {
      if (isStale()) return;
      if (currentWS === ws) currentWS = null;
      report.wsCloseCode = event.code;

      if (event.code === 4401) {
        debugError(report, "ws closed 4401 unauthorized");
        if (currentObjectUrl === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          currentObjectUrl = null;
        }
        safeResolve({ data: { detail: "Unauthorized" } });
        return;
      }

      if (event.code !== 1000) {
        debugError(report, "ws abnormal close", event.code, event.reason);
        logSummary(report, "ws abnormal close");
        if (currentObjectUrl === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          currentObjectUrl = null;
        }
        safeResolve(null);
      } else {
        debugLog(report, "ws closed normally (1000)");
      }
    };
  });
}

/** Attach to an HTMLAudioElement to log playback progress (call after playAudio) */
export function attachTTSPlaybackDebug(audio, label = "playback") {
  if (!TTS_DEBUG || !audio) return;

  const logState = (eventName) => {
    console.log(`[TTS-WS] ${label} ${eventName}`, {
      currentTime: audio.currentTime,
      duration: audio.duration,
      ended: audio.ended,
      paused: audio.paused,
      readyState: audio.readyState,
    });
  };

  ["loadedmetadata", "canplay", "playing", "waiting", "stalled", "ended", "error"].forEach(
    (evt) => audio.addEventListener(evt, () => logState(evt))
  );
}
